/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 24/03/22
 */

import { namedMemo } from '@/utils/namedMemo';
import {
    getAjv,
    getData,
    isVisible,
    JsonFormsCellRendererRegistryEntry,
    JsonFormsRendererRegistryEntry,
    JsonSchema,
    UISchemaElement,
} from '@jsonforms/core';
import { JsonFormsDispatch, useJsonForms } from '@jsonforms/react';
import { GridItem } from '@chakra-ui/react';
import { isEqual } from 'lodash';
import React from 'react';

interface ChakraLayoutElementsProps {
    elements: UISchemaElement[];
    schema?: JsonSchema;
    path?: string;
    enabled?: boolean;
    renderers?: JsonFormsRendererRegistryEntry[];
    cells?: JsonFormsCellRendererRegistryEntry[];
}

const ChakraLayoutElements = namedMemo(
    'ChakraLayoutElements',
    ({ elements, schema, path, enabled, renderers, cells }: ChakraLayoutElementsProps) => {
        return (
            <>
                {elements.flatMap((child, index) => {
                    const ctx = useJsonForms();
                    const ajv = getAjv({ jsonforms: { ...ctx } });
                    const data = getData({ jsonforms: { ...ctx } });
                    if (isVisible(child, data, path ?? '', ajv)) {
                        const childElement = (
                            <JsonFormsDispatch
                                uischema={child}
                                schema={schema}
                                path={path}
                                enabled={enabled}
                                renderers={renderers}
                                cells={cells}
                            />
                        );
                        const options = child.options;
                        if (
                            options &&
                            (options.colSpan ||
                                options.rowSpan ||
                                options.colStart ||
                                options.colEnd ||
                                options.rowStart ||
                                options.rowEnd)
                        ) {
                            return (
                                <GridItem
                                    key={`${path}-${index}`}
                                    colSpan={options.colSpan}
                                    rowSpan={options.rowSpan}
                                    colStart={options.colStart}
                                    colEnd={options.colEnd}
                                    rowStart={options.rowStart}
                                    rowEnd={options.rowEnd}
                                >
                                    {childElement}
                                </GridItem>
                            );
                        }
                        return <React.Fragment key={`${path}-${index}`}>{childElement}</React.Fragment>;
                    }
                })}
            </>
        );
    },
    isEqual,
);

export default ChakraLayoutElements;
